import { availableToApplyAud } from '@atink/domain';
import { bookingPriceAud } from '@/domain/pricing';
import type { Booking, Deposit } from '@/domain/types';

/**
 * ontheday-10 (L6a): ONE answer to "what does this appointment cost, what's on
 * file against it, and what's left to pay". The Floor row, the checkout header
 * and invoice, the payment step and the booking card each worked it out their
 * own way (a card with no day rate showed "—" while the Floor priced it from the
 * rate card; "deposit" meant paid in one place and applied-today in another;
 * the card's balance wasn't clamped; nothing subtracted earlier redemptions).
 *
 *  - priceAud:         the session price (day rate, else the artist's rate card);
 *  - depositOnFileAud: the deposit money free to use for THIS booking — paid in,
 *                      less what was already redeemed into OTHER bookings;
 *  - balanceAud:       price less what's on file, never below zero.
 *
 * It adds no new money figure to any screen (W9): it replaces the sums already shown.
 */
export interface BookingMoney {
  priceAud: number;
  depositOnFileAud: number;
  balanceAud: number;
}

type ArtistRates = Parameters<typeof bookingPriceAud>[1];

export function depositOnFileFor(deposit: Deposit | undefined, bookingId: string): number {
  if (!deposit) return 0;
  const intoThis = (deposit.ledger ?? []).reduce(
    (sum, e) => (e.kind === 'redeemed' && e.toBookingId === bookingId ? sum + e.amountAud : sum),
    0,
  );
  return Math.round((availableToApplyAud(deposit) + intoThis) * 100) / 100;
}

export function bookingMoney(booking: Booking, artist: ArtistRates, deposit: Deposit | undefined): BookingMoney {
  const priceAud = bookingPriceAud(booking, artist);
  const depositOnFileAud = depositOnFileFor(deposit, booking.id);
  return { priceAud, depositOnFileAud, balanceAud: Math.max(0, priceAud - depositOnFileAud) };
}
